/**
 * Aircraft geometry analysis — pure maths, no renderer.
 *
 * Given a cloud of vertex positions, work out which axis is the fuselage,
 * which end is the nose, and what scale factor brings the model to life size.
 *
 * THE ORIENTATION TRICK
 * The obvious approach — assume the longest axis is the fuselage — fails on a
 * Tomcat, whose 19.55 m span and 19.10 m length are within three per cent of
 * each other. The reliable signal is SYMMETRY. An aircraft is mirror-symmetric
 * about its centreline, so vertices along the SPAN axis are distributed
 * symmetrically and their skewness is near zero, while along the LENGTH axis
 * the distribution is strongly skewed: a thin nose at one end, engines and
 * tails at the other. Whichever horizontal axis is more skewed is the fuselage.
 *
 * Which end is the nose follows from the same asymmetry — the tail end is far
 * taller, because that is where the fins are.
 *
 * This lives apart from the scene-graph code so it can be tested against
 * synthetic aircraft in every orientation without a renderer. That matters:
 * a model facing the wrong way makes the jet FLY sideways, because the
 * quaternion assumes the nose points at -Z, and the physics will insist
 * nothing is wrong.
 */

/**
 * @param {number[]} samples flat xyz triples in the model's own frame
 * @param {number} trueLengthM  real-world length to scale toward
 */
export function analyseAircraftGeometry(samples, trueLengthM = 19.1) {
  if (!samples || samples.length < 24) {
    return { ok: false, reason: 'model has too little geometry to measure' }
  }

  const xs = []
  const ys = []
  const zs = []
  for (let i = 0; i < samples.length; i += 3) {
    xs.push(samples[i])
    ys.push(samples[i + 1])
    zs.push(samples[i + 2])
  }

  const range = (a) => {
    let lo = Infinity
    let hi = -Infinity
    for (const v of a) {
      if (v < lo) lo = v
      if (v > hi) hi = v
    }
    return { lo, hi, size: hi - lo }
  }

  const rx = range(xs)
  const rz = range(zs)

  // Which horizontal axis is the fuselage? The LESS SYMMETRIC one. Picking the
  // longer axis fails on a Tomcat: 19.55 m span against 19.10 m length is a
  // three per cent difference and a coin toss.
  const skewX = Math.abs(skewness(xs))
  const skewZ = Math.abs(skewness(zs))
  const lengthAxis = skewZ >= skewX ? 'z' : 'x'
  const lengthRange = lengthAxis === 'z' ? rz : rx
  const along = lengthAxis === 'z' ? zs : xs

  // Which end is the nose? The end with the smaller vertical extent, because
  // the other one has the fins on it.
  const band = 0.18
  const loCut = lengthRange.lo + lengthRange.size * band
  const hiCut = lengthRange.hi - lengthRange.size * band
  let loMinY = Infinity, loMaxY = -Infinity, hiMinY = Infinity, hiMaxY = -Infinity
  for (let i = 0; i < along.length; i++) {
    const a = along[i]
    const y = ys[i]
    if (a <= loCut) {
      if (y < loMinY) loMinY = y
      if (y > loMaxY) loMaxY = y
    } else if (a >= hiCut) {
      if (y < hiMinY) hiMinY = y
      if (y > hiMaxY) hiMaxY = y
    }
  }
  const loHeight = loMaxY - loMinY
  const hiHeight = hiMaxY - hiMinY
  const noseAtLowEnd = loHeight <= hiHeight

  // Rotation about Y that sends the nose to -Z.
  let yawDeg
  if (lengthAxis === 'z') yawDeg = noseAtLowEnd ? 0 : 180
  else yawDeg = noseAtLowEnd ? -90 : 90

  return {
    ok: true,
    lengthAxis,
    noseAtLowEnd,
    yawDeg,
    scale: trueLengthM / lengthRange.size,
    endHeights: { low: loHeight, high: hiHeight },
    skew: { x: skewX, z: skewZ },
    lengthBefore: lengthRange.size,
    spanBefore: (lengthAxis === 'z' ? rx : rz).size,
  }
}

/** Third standardised moment. Near zero means the distribution is symmetric. */
function skewness(values) {
  const n = values.length
  if (n < 8) return 0
  let mean = 0
  for (const x of values) mean += x
  mean /= n
  let m2 = 0
  let m3 = 0
  for (const x of values) {
    const d = x - mean
    m2 += d * d
    m3 += d * d * d
  }
  m2 /= n
  m3 /= n
  const sd = Math.sqrt(m2)
  return sd < 1e-9 ? 0 : m3 / (sd * sd * sd)
}

